import { renderToString } from "react-dom/server";
import { StaticRouter } from "react-router-dom/server";
import { App } from "./App";
import { FAQ } from "./content/faq";
import { FORMAT_HUBS } from "./content/formats";
import { PROVIDER_LIMITS } from "./content/limits";
import { ARTICLE_HUBS } from "./content/personas";

export interface RouteMeta {
  path: string;
  file: string;
  title: string;
  description: string;
  jsonLd?: unknown;
}

const NAME = "LimeShare";

export const routes: RouteMeta[] = [
  {
    path: "/",
    file: "index.html",
    title: "LimeShare — send large files free with zero-knowledge encryption",
    description:
      "Send files up to 3 GB for free. Encrypted in your browser with AES-256, no account, no tracking. Links self-destruct after 48 hours or on first download.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: NAME,
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description: "Zero-knowledge encrypted file transfer. Files are encrypted in the browser and self-destruct.",
      sameAs: [
        "https://dataverse.harvard.edu/dataverse/limeshare",
        "https://dataverse.harvard.edu/dataset.xhtml?persistentId=doi:10.7910/DVN/M6TLOS",
        "https://doi.org/10.7910/DVN/M6TLOS",
        "https://doi.org/10.5281/zenodo.23671752",
        "https://doi.org/10.5281/zenodo.23671754",
        "https://doi.org/10.5281/zenodo.23671756",
        "https://doi.org/10.5281/zenodo.23671758",
        "https://doi.org/10.5281/zenodo.23671762",
        "https://zenodo.org/records/23671752",
      ],
      featureList: [
        "End-to-end encryption (AES-256-GCM, LSC1)",
        "No account required",
        "Up to 3 GB per file free",
        "Burn after first download",
        "Password protection (Argon2id)",
        "Streaming download for multi-gigabyte files",
        "LimeDrop direct browser-to-browser P2P",
        "Certified Destruction verification receipts",
      ],
    },
  },
  {
    path: "/drop",
    file: "drop.html",
    title: "LimeDrop — Send Files Directly Browser-to-Browser (WebRTC P2P)",
    description: "Transfer files directly between devices on local Wi-Fi or WebRTC. 0 cloud storage, zero egress, uncapped local transfer speeds.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to send files directly with LimeDrop",
      step: [
        { "@type": "HowToStep", name: "Select files", text: "Drop files into LimeDrop on your sending device." },
        { "@type": "HowToStep", name: "Share P2P link", text: "Open the generated link or QR code on your receiving device." },
        { "@type": "HowToStep", name: "Stream directly", text: "Files stream directly device-to-device through encrypted WebRTC DataChannels." },
      ],
    },
  },
  {
    path: "/products/teams",
    file: "products-teams.html",
    title: "LimeShare Teams — 25 People, One Price ($149/yr, 2 TB Shared Pool)",
    description: "Stop paying per-seat fees. Empower up to 25 team members with 2 TB of shared zero-knowledge encrypted transfer storage for one flat yearly price.",
  },
  {
    path: "/products/exceed",
    file: "products-exceed.html",
    title: "LimeShare Exceed — Send Up to 1 TB Without Signing Up",
    description: "Send 50 GB ($2.49), 200 GB ($4.49), or 1 TB ($8.99) in a single encrypted transfer without creating an account or buying a subscription.",
  },
  {
    path: "/products/extend",
    file: "products-extend.html",
    title: "LimeShare Extend — Keep Files Alive for Up to 1 Year Without Signup",
    description: "Extend any file transfer up to 5 GB for 6 months ($1.99) or 1 full year ($2.99) with unlimited downloads. Works for both senders and recipients.",
  },
  {
    path: "/request-files",
    file: "request-files.html",
    title: "Request Files from Clients Without Login — Free Dropbox File Request Alternative",
    description: "Create a zero-knowledge Receive-Files portal link in 5 seconds. Collect up to 5 GB of files or folders from clients without registration.",
  },
  {
    path: "/video-review-tool",
    file: "video-review-tool.html",
    title: "Frame-Accurate Video & Audio Review Player — Free Frame.io Alternative",
    description: "Share 4K MP4, ProRes MOV, and WAV audio with frame-accurate timestamp comments in the browser. Zero re-encoding, end-to-end encrypted, no login.",
  },
  {
    path: "/developers",
    file: "developers.html",
    title: "LimeShare for Developers & CLI — Zero-Knowledge transfer.sh Alternative",
    description: "Send encrypted files from Linux, macOS, Windows terminal or GitHub Actions CI/CD using the open LSC1 AES-256-GCM streaming protocol.",
  },
  {
    path: "/sell-digital-files",
    file: "sell-digital-files.html",
    title: "Sell Digital Files & 4K Footage via Pay-to-Decrypt Links (No KYC)",
    description: "Lock client file deliveries behind a crypto paywall. Clients preview files and sizes in browser; full master download unlocks automatically upon payment.",
  },
  {
    path: "/research",
    file: "research.html",
    title: "Academic Research, LSC1 Cryptography & Zero-Egress RDM Hub (Harvard & CERN DOIs)",
    description: "Open-access LSC1 streaming encryption specification, Harvard Dataverse DOI (10.7910/DVN/M6TLOS), 5 CERN Zenodo DOIs, and WebCrypto benchmark corpus.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ScholarlyArticle",
          headline: "LSC1: Streaming Authenticated Encryption with Associated Data (AEAD), Browser Memory Bounds, and Zero-Egress Cloud Distribution Benchmark",
          author: { "@type": "Organization", name: "LimeShare Cryptography & Distributed Systems Lab" },
          publisher: { "@type": "Organization", name: NAME },
          datePublished: "2026-10-26",
          url: "https://limeshare.org/research",
          sameAs: [
            "https://doi.org/10.7910/DVN/M6TLOS",
            "https://doi.org/10.5281/zenodo.23671752",
          ],
        },
        {
          "@type": "Dataset",
          name: "LimeShare LSC1 Streaming AEAD & Platform Attachment Limit Benchmark Dataset (2026)",
          description: "Empirical WebCrypto AES-256-GCM throughput, browser heap memory bounds, and global platform attachment ceilings across 40+ services.",
          license: "https://creativecommons.org/licenses/by/4.0/",
          identifier: ["https://doi.org/10.7910/DVN/M6TLOS", "https://doi.org/10.5281/zenodo.23671752"],
          sameAs: "https://dataverse.harvard.edu/dataset.xhtml?persistentId=doi:10.7910/DVN/M6TLOS",
          creator: { "@type": "Organization", name: "LimeShare Cryptography & Distributed Systems Lab" },
          distribution: [
            {
              "@type": "DataDownload",
              encodingFormat: "text/csv",
              contentUrl: "https://limeshare.org/data/limeshare-lsc1-benchmarks.csv",
            },
            {
              "@type": "DataDownload",
              encodingFormat: "application/pdf",
              contentUrl: "https://limeshare.org/data/LimeShare-LSC1-Cryptographic-Protocol-Working-Paper.pdf",
            },
          ],
        },
      ],
    },
  },
  {
    path: "/papers",
    file: "papers.html",
    title: "Academic PDF Working Papers, Cryptographic Compendiums & CERN / Harvard DOIs — LimeShare",
    description: "Download 5 branded LimeShare PDF 1.4 working papers on LSC1 AES-256-GCM STREAM encryption, academic RDM, WebRTC P2P, and GDPR/HIPAA erasure.",
  },
  {
    path: "/data",
    file: "data.html",
    title: "Open Cryptographic Benchmark Dataset & LSC1 Test Vectors (CSV, JSONL, BibTeX) — LimeShare",
    description: "Download CC BY 4.0 WebCrypto AES-256-GCM vs. WebRTC P2P throughput benchmarks, 2026 platform attachment limits, Frictionless datapackage.json, and Harvard/CERN DOIs.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: "LimeShare LSC1 Cryptographic Protocol & Zero-Egress File Transfer Benchmark Dataset (2026)",
      description: "Empirical throughput, memory utilization, and platform attachment-limit datasets in UTF-8 CSV, JSONL, and Frictionless Data Package format.",
      url: "https://limeshare.org/data",
      sameAs: "https://dataverse.harvard.edu/dataset.xhtml?persistentId=doi:10.7910/DVN/M6TLOS",
      identifier: ["https://doi.org/10.7910/DVN/M6TLOS", "https://doi.org/10.5281/zenodo.23671752"],
      license: "https://creativecommons.org/licenses/by/4.0/",
      isAccessibleForFree: true,
      creator: { "@type": "Organization", name: "LimeShare Cryptography & Distributed Systems Lab", url: "https://limeshare.org" },
      distribution: [
        { "@type": "DataDownload", encodingFormat: "text/csv", contentUrl: "https://limeshare.org/data/limeshare-lsc1-benchmarks.csv" },
        { "@type": "DataDownload", encodingFormat: "application/x-ndjson", contentUrl: "https://limeshare.org/data/limeshare-lsc1-benchmarks.jsonl" },
        { "@type": "DataDownload", encodingFormat: "application/pdf", contentUrl: "https://limeshare.org/data/LimeShare-LSC1-Cryptographic-Protocol-Working-Paper.pdf" },
      ],
    },
  },
  {
    path: "/citation",
    file: "citation.html",
    title: "Cite LimeShare LSC1 Protocol & Benchmark Dataset (BibTeX, APA, IEEE)",
    description: "Official academic citations (BibTeX, APA 7th, IEEE) and working paper downloads for the LimeShare LSC1 streaming encryption protocol.",
  },

  // Multilingual Regional Homepages & Hubs (12 Languages)
  {
    path: "/ru",
    file: "ru.html",
    title: "LimeShare — Отправить большие файлы до 5 ГБ бесплатно без регистрации",
    description: "Передача больших файлов и папок до 5 ГБ бесплатно без регистрации. Шифрование AES-256 в браузере, до 30 дней хранения, прямой P2P режим LimeDrop.",
  },
  {
    path: "/tr",
    file: "tr.html",
    title: "LimeShare — Ücretsiz 5 GB Büyük Dosya Gönderme (Üyeliksiz & Şifreli)",
    description: "Üye olmadan 5 GB'a kadar büyük dosya ve klasörleri ücretsiz gönderin. Tarayıcı içi AES-256 şifreleme, 1 TB Exceed ve doğrudan P2P aktarım.",
  },
  {
    path: "/de",
    file: "de.html",
    title: "LimeShare — Große Dateien bis 5 GB kostenlos versenden ohne Anmeldung",
    description: "Große Dateien und Ordner bis 5 GB kostenlos ohne Registrierung verschicken. Ende-zu-Ende AES-256 Verschlüsselung im Browser und P2P LimeDrop.",
  },
  {
    path: "/fr",
    file: "fr.html",
    title: "LimeShare — Envoyer de gros fichiers jusqu'à 5 Go gratuitement sans inscription",
    description: "Transférez des fichiers volumineux et des dossiers jusqu'à 5 Go gratuitement sans compte. Chiffrement Zero-Knowledge AES-256 dans le navigateur.",
  },
  {
    path: "/es",
    file: "es.html",
    title: "LimeShare — Enviar archivos grandes de hasta 5 GB gratis sin registro",
    description: "Envía archivos grandes y carpetas de hasta 5 GB gratis sin registrarte. Cifrado AES-256 de conocimiento cero en el navegador y modo P2P.",
  },
  {
    path: "/nl",
    file: "nl.html",
    title: "LimeShare — Grote bestanden tot 5 GB gratis versturen zonder registratie",
    description: "Verstuur grote bestanden en mappen tot 5 GB gratis zonder account met Zero-Knowledge AES-256 browser-encryptie.",
  },
  {
    path: "/it",
    file: "it.html",
    title: "LimeShare — Inviare file grandi fino a 5 GB gratis senza registrazione",
    description: "Invia file di grandi dimensioni e cartelle fino a 5 GB gratis senza account con crittografia Zero-Knowledge AES-256 nel browser.",
  },
  {
    path: "/pt",
    file: "pt.html",
    title: "LimeShare — Enviar ficheiros grandes até 5 GB grátis sem registo",
    description: "Envie ficheiros grandes e pastas até 5 GB gratuitamente sem criar conta. Encriptação Zero-Knowledge AES-256 no navegador.",
  },
  {
    path: "/pl",
    file: "pl.html",
    title: "LimeShare — Wysyłanie dużych plików do 5 GB za darmo bez rejestracji",
    description: "Przesyłaj duże pliki i foldery do 5 GB za darmo bez konta. Szyfrowanie Zero-Knowledge AES-256 w przeglądarce.",
  },
  {
    path: "/ro",
    file: "ro.html",
    title: "LimeShare — Trimite fișiere mari de până la 5 GB gratuit fără înregistrare",
    description: "Trimite fișiere mari și dosare de până la 5 GB gratuit fără cont cu criptare Zero-Knowledge AES-256 în browser.",
  },
  {
    path: "/hr",
    file: "hr.html",
    title: "LimeShare — Slanje velikih datoteka do 5 GB besplatno bez registracije",
    description: "Pošaljite velike datoteke i mape do 5 GB besplatno bez računa uz Zero-Knowledge AES-256 enkripciju u pregledniku.",
  },
  {
    path: "/ultimate-file-transfer-guide",
    file: "ultimate-file-transfer-guide.html",
    title: "The Ultimate Guide to Large File Transfer in 2026: Protocols, Security & Egress",
    description: "4,200-word comprehensive reference: network protocol physics, cloud egress economics, zero-knowledge STREAM cryptography, platform attachment limits, and device transfers.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: "The Ultimate Guide to Large File Transfer in 2026: Protocols, Security, Compression & Zero-Egress Economics",
      author: { "@type": "Organization", name: NAME },
      publisher: { "@type": "Organization", name: NAME },
      datePublished: "2026-09-23",
      dateModified: "2026-09-23",
    },
  },

  // Interactive Tools
  {
    path: "/tools/encrypt-file",
    file: "tools-encrypt-file.html",
    title: "Offline File Encryptor (AES-256-GCM + Argon2id) — Free In-Browser",
    description: "Encrypt and decrypt any file locally in your browser with AES-256-GCM and Argon2id passwords. 100% offline WebCrypto execution. 0 bytes uploaded.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "LimeShare Offline File Encryptor",
      applicationCategory: "SecurityApplication",
      browserRequirements: "Requires WebCrypto and WebAssembly",
    },
  },
  {
    path: "/tools/upload-time-calculator",
    file: "tools-upload-time-calculator.html",
    title: "File Upload & Download Time Calculator (Broadband & 5G)",
    description: "Calculate how long it takes to upload or download a file based on size and internet bandwidth (Mbps). Live comparison for fiber, Wi-Fi, 4G, 5G.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "File Upload Time Calculator",
      applicationCategory: "UtilitiesApplication",
      browserRequirements: "Requires JavaScript",
    },
  },
  {
    path: "/tools/file-checksum",
    file: "tools-file-checksum.html",
    title: "Online SHA-256 Checksum Verifier — Fast Client-Side Hash",
    description: "Verify file integrity with SHA-256 checksums in your browser. 100% client-side WebCrypto execution. Fast, private, zero byte uploads.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Online SHA-256 Checksum Tool",
      applicationCategory: "SecurityApplication",
      browserRequirements: "Requires WebCrypto API",
    },
  },

  // Certified Destruction Verifier
  {
    path: "/verify-receipt",
    file: "verify-receipt.html",
    title: "Certified Destruction Verifier — Cryptographic Proof of Data Erasure",
    description: "Verify the authenticity of a cryptographic Certificate of Destruction. Validates that confidential files were permanently erased from encrypted cloud storage.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "LimeShare Certified Destruction Verifier",
      applicationCategory: "SecurityApplication",
    },
  },

  {
    path: "/how-it-works",
    file: "how-it-works.html",
    title: "How LimeShare works — client-side encryption, direct-to-storage upload",
    description:
      "Step by step: key generation in your browser, streaming AES-256-GCM encryption, direct upload to storage, links that carry the key in the URL fragment, automatic deletion.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to send an encrypted file with LimeShare",
      step: [
        { "@type": "HowToStep", name: "Pick files", text: "Drop up to 3 files of up to 3 GB each into limeshare.org." },
        { "@type": "HowToStep", name: "Encrypt and upload", text: "Click Encrypt & get link. Files are encrypted in your browser and uploaded directly to storage." },
        { "@type": "HowToStep", name: "Share the link", text: "Copy the link or QR code. The key is in the part after # and never reaches the server." },
        { "@type": "HowToStep", name: "Recipient decrypts", text: "The recipient opens the link; their browser verifies and decrypts the files locally." },
      ],
    },
  },
  {
    path: "/faq",
    file: "faq.html",
    title: "LimeShare FAQ — limits, encryption, expiry, passwords",
    description: "Answers about sending large files with LimeShare: size limits, zero-knowledge encryption, burn on download, passwords, storage location and deletion.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  },
  {
    path: "/send-large-files",
    file: "send-large-files.html",
    title: "How to send large files in 2026 — free, fast, encrypted",
    description:
      "Email caps attachments at 20–25 MB. Compare transfer links, cloud drives, messengers and compression — and send files up to 3 GB free with end-to-end encryption.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "How to send large files (2026 guide)",
      author: { "@type": "Organization", name: NAME },
      publisher: { "@type": "Organization", name: NAME },
    },
  },
  {
    path: "/email-attachment-size-limits",
    file: "email-attachment-size-limits.html",
    title: "Email attachment size limits 2026: Gmail, Outlook, iCloud, WhatsApp, Telegram, Discord, Slack",
    description: "Reference table of attachment limits for Gmail (25 MB), Outlook (20 MB), WhatsApp (2 GB), Telegram, Discord, Slack and Teams — and what to do when a file is too large.",
  },
  {
    path: "/wetransfer-alternative",
    file: "wetransfer-alternative.html",
    title: "WeTransfer alternative with end-to-end encryption — LimeShare",
    description: "Side-by-side: LimeShare vs WeTransfer. Zero-knowledge encryption, no account, 3 GB per file free, burn on download, free password protection, open-source client.",
  },
  {
    path: "/encrypted-file-transfer",
    file: "encrypted-file-transfer.html",
    title: "Encrypted file transfer explained: TLS vs at-rest vs end-to-end",
    description: "What 'encrypted' really means for file sharing services, how to verify zero-knowledge claims, and how LimeShare's LSC1 streaming encryption works.",
  },
  {
    path: "/send-files-anonymously",
    file: "send-files-anonymously.html",
    title: "Send files anonymously — no account, no email, no app",
    description: "How to share files without revealing who you are: what LimeShare never collects, what stays visible, and practical tips for private handoffs.",
  },

  // Programmatic format hubs with FAQPage and HowTo schemas
  ...FORMAT_HUBS.map((hub): RouteMeta => ({
    path: `/${hub.slug}`,
    file: `${hub.slug}.html`,
    title: hub.title,
    description: hub.description,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "HowTo",
          name: `How to send ${hub.formatName} files online`,
          step: [
            { "@type": "HowToStep", name: "Select files", text: `Choose your ${hub.formatName} file up to 3 GB.` },
            { "@type": "HowToStep", name: "Browser Encrypt", text: `LimeShare encrypts your ${hub.formatName} client-side with AES-256-GCM.` },
            { "@type": "HowToStep", name: "Deliver with link", text: "Send the self-destructing link or QR code to your recipient." },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: hub.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  })),

  // Programmatic Provider Limits Hubs with FAQPage
  ...PROVIDER_LIMITS.map((item): RouteMeta => ({
    path: `/${item.slug}`,
    file: `${item.slug}.html`,
    title: item.title,
    description: item.description,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: item.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  })),

  // Programmatic Article Hubs (Devices, Personas, Competitor Alternatives)
  ...ARTICLE_HUBS.map((hub): RouteMeta => ({
    path: `/${hub.slug}`,
    file: `${hub.slug}.html`,
    title: hub.title,
    description: hub.description,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "HowTo",
          name: hub.h1,
          step: hub.steps.map((st) => ({
            "@type": "HowToStep",
            name: st.name,
            text: st.text,
          })),
        },
        {
          "@type": "FAQPage",
          mainEntity: hub.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  })),

  {
    path: "/security",
    file: "security.html",
    title: "LimeShare security model — what we protect and what we cannot",
    description: "How LimeShare encrypts files, what the server knows (and does not), threat model, verifiability and how to report security issues.",
  },
  { path: "/terms", file: "terms.html", title: "LimeShare Terms of Service", description: "Terms of use, acceptable use policy and notice-and-takedown procedure for LimeShare." },
  { path: "/privacy", file: "privacy.html", title: "LimeShare Privacy Policy", description: "What data LimeShare processes, for how long, and what it never has: keys, file contents, names or raw IP addresses." },
];

export function render(url: string): string {
  return renderToString(
    <StaticRouter location={url}>
      <App />
    </StaticRouter>,
  );
}
